// hooks/useSampleDataQueries.jsx
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tvApi } from "../api/services.jsx";

// Enum 변환 함수 - 백엔드 TransitionType enum에 맞게 수정
const getTransitionTypeEnum = (type) => {
  const enumMap = {
    fade: "FADE",
    slideLeft: "LEFT_SLIDE",
    slideRight: "RIGHT_SLIDE",
    slideUp: "UP_SLIDE",
    slideDown: "DOWN_SLIDE",
    zoomIn: "ZOOM_IN",
    zoomOut: "ZOOM_OUT",
    rotateLeft: "ROTATE_LEFT",
    rotateRight: "ROTATE_RIGHT",
    flipHorizontal: "HORIZONTAL_FLIP",
    flipVertical: "VERTICAL_FLIP",
    blinds: "BLIND",
    cube: "CUBE_ROTATION",
  };
  return enumMap[type] || "FADE";
};

// ContentType enum 변환
const getContentTypeEnum = (type) => {
  switch (type) {
    case "image":
      return "IMAGE";
    case "video":
      return "VIDEO";
    case "website":
      return "WEBSITE";
    default:
      return "IMAGE";
  }
};

// 백엔드 스펙에 맞게 수정된 샘플 데이터 생성 함수
const createSampleData = (currentTVData) => {
  // 임시 ID 생성 함수
  const generateTempId = () => Date.now() + Math.random();

  // ContentManagement가 바로 사용할 수 있는 contents 배열 생성
  const sampleContents = [
    // 1. 회사 로고 이미지
    {
      id: generateTempId(),
      mappingId: null,
      sortOrder: 1,
      playDuration: 10,
      name: "회사 로고",
      isMusicPlay: false,
      content: {
        id: generateTempId(),
        title: "회사 로고",
        contentType: "IMAGE", // 백엔드 enum
        type: "image", // 프론트엔드용
        url: "https://jirandata.co.kr/_files/board/20250106//378c023186b6236373dd3fed0002a633.png",
        fileName: null,
        videoType: null,
        binaryData: null,
        mimeType: null,
      },
    },

    // 2. AXworks 웹사이트
    {
      id: generateTempId(),
      mappingId: null,
      sortOrder: 2,
      playDuration: 15,
      name: "AXworks 웹사이트",
      isMusicPlay: false,
      content: {
        id: generateTempId(),
        title: "AXworks 웹사이트",
        contentType: "WEBSITE", // 백엔드 enum
        type: "website", // 프론트엔드용
        url: "https://axworks.co.kr/",
        fileName: null,
        videoType: null,
        binaryData: null,
        mimeType: null,
      },
    },

    // 3. 홍보 영상 YouTube
    {
      id: generateTempId(),
      mappingId: null,
      sortOrder: 3,
      playDuration: 60,
      name: "홍보 영상",
      isMusicPlay: true, // 영상은 음악 재생 활성화
      content: {
        id: generateTempId(),
        title: "홍보 영상",
        contentType: "VIDEO", // 백엔드 enum
        type: "video", // 프론트엔드용
        url: "https://www.youtube.com/watch?v=hHihDjazeEU",
        fileName: null,
        videoType: "youtube",
        binaryData: null,
        mimeType: null,
      },
    },

    // 4. 지란지교데이터 웹사이트
    {
      id: generateTempId(),
      mappingId: null,
      sortOrder: 4,
      playDuration: 15,
      name: "지란지교데이터 웹사이트",
      isMusicPlay: false,
      content: {
        id: generateTempId(),
        title: "지란지교데이터 웹사이트",
        contentType: "WEBSITE", // 백엔드 enum
        type: "website", // 프론트엔드용
        url: "https://jirandata.co.kr/sub0201_list",
        fileName: null,
        videoType: null,
        binaryData: null,
        mimeType: null,
      },
    },
  ];

  // 백엔드 전송용 contentMappings 배열 생성
  const backendContentMappings = sampleContents.map((item) => ({
    id: item.id,
    mappingId: item.mappingId,
    sortOrder: item.sortOrder,
    playDuration: item.playDuration,
    name: item.name,
    isMusicPlay: item.isMusicPlay,

    // 백엔드 전송용 contentRequest
    contentRequest: {
      contentType: item.content.contentType,
      sortOrder: item.sortOrder,
      playDuration: item.playDuration,
      isMusicPlay: item.isMusicPlay,
      binaryData: item.content.binaryData,
      url: item.content.url,
      existingContentId: null,
    },

    // 프론트엔드 표시용 content (기존 구조 유지)
    content: item.content,
  }));

  return {
    // PIN 설정과 전환 효과는 기존 값 유지
    pin: currentTVData?.authenticationCode || null,
    transitionType: getTransitionTypeEnum(
      currentTVData?.transitionType || "fade"
    ),
    transitionDuration: currentTVData?.transitionDuration || 1,

    // 배경음악 설정 - 백엔드 필드명에 맞게 수정
    isBgmEnabled: true, // 샘플 데이터는 배경음악 활성화

    // 배경음악만 샘플 데이터로 교체 - 백엔드 TvBgmMappingRequest 스펙에 맞게
    bgmMappings: [
      {
        // 드래그앤드롭을 위해 유효한 ID 필요
        id: generateTempId(),
        mappingId: null,
        sortOrder: 1,
        playDuration: 180,
        volume: 50,
        name: "샘플 배경음악",

        // BGM 정보 - BgmRequest (필수)
        bgmRequest: {
          sortOrder: 1,
          playDuration: 180,
          volume: 50,
          binaryData: null,
          url: "https://www.youtube.com/watch?v=cjr-wbV0_QA",
          existingBgmId: null,
        },

        // 프론트엔드 표시용 추가 정보 (백엔드로 전송되지 않음)
        bgm: {
          id: generateTempId(),
          title: "샘플 배경음악",
          type: "youtube",
          url: "https://www.youtube.com/watch?v=cjr-wbV0_QA",
        },
      },
    ],

    // ContentManagement가 직접 사용할 수 있는 contents 배열
    contents: sampleContents,

    // 백엔드 전송용 contentMappings (기존 호환성 유지)
    contentMappings: backendContentMappings,
  };
};

// 샘플 데이터 로드 mutation - API 호출 없이 브라우저 상태만 업데이트
export const useLoadSampleData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ currentTVData }) => {
      // API 호출하지 않고 샘플 데이터만 생성해서 반환
      const sampleData = createSampleData(currentTVData);

      // 브라우저 상태 업데이트를 위한 데이터 반환 - 백엔드 응답 구조에 맞게
      return {
        success: true,
        sampleTVData: {
          ...currentTVData,

          // 백엔드 필드명에 맞게 수정
          isBgmEnabled: sampleData.isBgmEnabled,
          bgmMappings: sampleData.bgmMappings,

          // ContentManagement가 바로 사용할 수 있는 contents 필드
          contents: sampleData.contents,

          // 백엔드 전송용 contentMappings (기존 호환성 유지)
          contentMappings: sampleData.contentMappings,

          // 기존 필드들 유지
          pin: sampleData.pin,
          transitionType: sampleData.transitionType,
          transitionDuration: sampleData.transitionDuration,
        },
        message:
          "샘플 데이터가 브라우저에 로드되었습니다. 설정 저장을 클릭하여 실제 적용하세요.",
      };
    },
    onSuccess: (data, variables) => {
      // React Query 캐시는 업데이트하지 않음 - 브라우저 상태만 업데이트
    },
    onError: (error) => {
      // 에러 처리는 상위 컴포넌트에서 처리
    },
  });
};

// 샘플 데이터 검증 (옵션 - 필요시 사용)
export const useSampleDataValidation = () => {
  return async (tvId) => {
    try {
      // TV가 존재하는지 확인
      const result = await tvApi.getTv(tvId);

      if (!result.success) {
        throw new Error("현황판을 찾을 수 없습니다.");
      }

      // TV가 활성화되어 있는지 확인
      if (!result.data.enabled) {
        throw new Error(
          "비활성화된 현황판에는 샘플 컨텐츠를 로드할 수 없습니다."
        );
      }

      return { isValid: true, tvData: result.data };
    } catch (error) {
      throw new Error(error.message || "TV 상태 확인에 실패했습니다.");
    }
  };
};

// 추가: 샘플 데이터 미리보기 함수 (실제 저장 전 데이터 구조 확인용)
export const previewSampleData = (currentTVData) => {
  const sampleData = createSampleData(currentTVData);
  return sampleData;
};
